// Batch E3 — Deal Pipeline Kanban view.
//
// Desktop: 8 horizontal columns with drag-and-drop powered by @dnd-kit.
// Mobile (<md): columns stack as collapsible accordions, drag disabled
// (touch drag is too fiddly for accurate stage moves on small screens).
//
// Data flows through useKanban() — a module-level cache identical in
// shape to usePipelineCounts(). On a successful drop:
//   1. moveCardOptimistically() mutates the cache in place
//   2. PATCH /api/deals/{id}/stage fires
//   3. on success: invalidatePipelineCounts() so tabs + dashboard widget
//      stay in sync
//   4. on failure: revertCardMove() + toast.error()

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { ChevronDown, ChevronUp, Inbox, Plus, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { useStages } from "@/components/pipeline/StageBadge";
import useKanban, {
  findCardById,
  invalidateKanban,
  moveCardOptimistically,
  revertCardMove,
} from "@/lib/useKanban";
import { invalidatePipelineCounts } from "@/lib/usePipelineCounts";
import { useFmt } from "@/lib/useFmt";
import { PropertyDetailLine } from "@/components/property/PropertyMeta";
import RoleDot from "@/components/deal/RoleDot";
import { formatPsfShort } from "@/lib/priceNormalisation";

// ─── Helpers ────────────────────────────────────────────────────────────

function money(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

// ─── Card ───────────────────────────────────────────────────────────────

function DealCard({ deal, onClick, dragging = false, listeners, attributes, setNodeRef, style }) {
  const fmt = useFmt();
  // Phase 36 Batch K — Kanban cards read role-aware metrics. Falls back to
  // legacy fields for any deal whose backend response hasn't populated the
  // new block yet (e.g. mid-rollout, an old WS cache).
  const mar = deal.metrics_at_role;
  const cashflow = Number(mar?.monthly_cash_flow ?? deal.monthly_cash_flow ?? deal.monthly_rent ?? 0);
  const cashflowColor = cashflow < 0 ? "text-rose-600" : "text-emerald-600";
  const score = mar?.deal_score ?? deal.deal_score;
  const address = deal.property?.address || "—";
  // Phase 47.3 — compact PSF subtitle anchored on the canonical asking
  // price (so the kanban card reflects the seller's listed PSF even if
  // a counter has been filed).
  const askPx =
    deal.original_purchase_price ||
    deal.property?.asking_price ||
    deal.purchase_price ||
    0;
  const psfStr = formatPsfShort(askPx, deal.property?.square_feet);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={(e) => {
        // Don't treat drag-start clicks as a navigation. dnd-kit's PointerSensor
        // already filters out micro-movements, so a real click here is intent.
        if (e.defaultPrevented) return;
        onClick && onClick();
      }}
      data-testid={`kanban-card-${deal.id}`}
      className={`group cursor-grab active:cursor-grabbing rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:shadow-md hover:ring-1 hover:ring-slate-200 ${
        dragging ? "rotate-1 opacity-80 shadow-lg" : ""
      }`}
    >
      <div className="flex items-center gap-2">
        {/* Phase 36 Batch K — role indicator dot (buyer indigo / seller amber) */}
        <RoleDot metrics={mar} role={deal.role} testid={`role-dot-kanban-${deal.id}`} />
        <p className="truncate text-sm font-semibold text-slate-900">{address}</p>
      </div>
      {/* Detail line only — Kanban cards stay compact (no description) */}
      <PropertyDetailLine d={deal} fmt={fmt} />
      {psfStr && (
        <p
          data-testid={`kanban-psf-${deal.id}`}
          className="mt-0.5 truncate text-[10px] text-slate-400"
        >
          {psfStr}
        </p>
      )}
      <div className="mt-2 flex items-center justify-between">
        <span className={`text-xs font-semibold ${cashflowColor}`}>
          {money(cashflow)}/mo
        </span>
        {Number.isFinite(Number(score)) && (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-700">
            Score {Math.round(Number(score))}
          </span>
        )}
      </div>
      {/* Phase 36 Batch Q — payback line. Single small gray line so the
          card stays compact. "Never" when annual CF ≤ 0. */}
      <div
        data-testid={`kanban-payback-${deal.id}`}
        className="mt-1 text-[10px] text-slate-500"
      >
        Payback:{" "}
        <span className={mar?.payback_period_years == null ? "text-slate-400" : "tabular-nums text-slate-600"}>
          {mar?.payback_period_years == null
            ? "Never"
            : `${Number(mar.payback_period_years).toFixed(1)} yrs`}
        </span>
      </div>
    </div>
  );
}

function SortableCard({ deal, onClick }) {
  const { setNodeRef, listeners, attributes, isDragging, transform } = useDraggable({
    id: deal.id,
    data: { deal },
  });
  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 50 : "auto",
  } : undefined;
  return (
    <DealCard
      deal={deal}
      onClick={onClick}
      dragging={isDragging}
      setNodeRef={setNodeRef}
      style={style}
      listeners={listeners}
      attributes={attributes}
    />
  );
}

// ─── Column ────────────────────────────────────────────────────────────
//
// Phase 36 P0 follow-up #3 — header / pill / footer ALWAYS render,
// regardless of loading or error state. The deal-list area shows a
// skeleton during initial fetch, an empty-state icon on success+0
// deals, or the card list. This guarantees the user never sees
// columns with no structure.
function KanbanColumn({ stage, column, navigate, loading = false }) {
  const { setNodeRef, isOver } = useDroppable({ id: `column-${stage.id}`, data: { stage: stage.id } });
  const accentBar = stage.colors.bg.replace("-100", "-500"); // bg-slate-500 etc.
  const isEmpty = !loading && (!column.deals || column.deals.length === 0);
  const showMoreLink = !loading && column.truncated;

  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-column-${stage.id}`}
      className={`flex h-full w-72 flex-shrink-0 flex-col rounded-xl border bg-slate-50 transition-colors ${
        isOver ? "border-indigo-300 bg-indigo-50/40 ring-2 ring-indigo-100" : "border-slate-200"
      } scroll-snap-align-start`}
    >
      <div className={`h-1 rounded-t-xl ${accentBar}`} aria-hidden="true" />
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2.5 rounded-t-xl">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${accentBar}`} aria-hidden="true" />
          <span className={`text-sm font-semibold ${stage.colors.text}`}>{stage.label}</span>
          <span
            className="rounded-full bg-white px-1.5 text-[10px] font-semibold text-slate-600"
            data-testid={`kanban-column-count-${stage.id}`}
          >
            {column.count ?? 0}
          </span>
        </div>
        <button
          type="button"
          disabled
          aria-disabled="true"
          title="Coming soon"
          className="rounded-md p-1 text-slate-400 transition-colors hover:bg-white hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2" style={{ maxHeight: "calc(100vh - 240px)" }}>
        {loading ? (
          /* Loading skeleton lives INSIDE the column so the header
             above is still visible. 3 placeholder cards = realistic
             density without being noisy. */
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="h-16 w-full animate-pulse rounded-xl bg-slate-200/60"
                aria-hidden="true"
              />
            ))}
          </>
        ) : isEmpty ? (
          <div className="m-auto flex flex-col items-center gap-1.5 py-6 text-center text-xs text-slate-400">
            <Inbox className="h-5 w-5" />
            <span>No deals in this stage</span>
          </div>
        ) : (
          column.deals.map((d) => (
            <SortableCard
              key={d.id}
              deal={d}
              onClick={() => navigate(`/deals/${d.id}`)}
            />
          ))
        )}
        {showMoreLink && (
          <button
            type="button"
            onClick={() => navigate(`/portfolio?stage=${stage.id}`)}
            data-testid={`kanban-column-view-all-${stage.id}`}
            className="mt-1 w-full rounded-md py-1.5 text-center text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
          >
            View all in Portfolio →
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Mobile accordion ──────────────────────────────────────────────────

function MobileAccordion({ stage, column, navigate, expanded, onToggle, loading = false }) {
  const accentBar = stage.colors.bg.replace("-100", "-500");
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`kanban-accordion-${stage.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-3"
      >
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${accentBar}`} aria-hidden="true" />
          <span className={`text-sm font-semibold ${stage.colors.text}`}>{stage.label}</span>
          <span className="rounded-full bg-slate-100 px-1.5 text-[10px] font-semibold text-slate-600">
            {column.count ?? 0}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />}
      </button>
      {expanded && (
        <div className="flex flex-col gap-2 border-t border-slate-200 p-2">
          {loading ? (
            /* Phase 36 P0 follow-up #3 — loading skeleton lives inside
               the expanded body so header + count pill stay visible. */
            <>
              {Array.from({ length: 2 }).map((_, i) => (
                <div
                  key={i}
                  className="h-16 w-full animate-pulse rounded-xl bg-slate-200/60"
                  aria-hidden="true"
                />
              ))}
            </>
          ) : (column.deals || []).length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-slate-400">No deals in this stage</p>
          ) : (
            column.deals.map((d) => (
              <DealCard
                key={d.id}
                deal={d}
                onClick={() => navigate(`/deals/${d.id}`)}
              />
            ))
          )}
          {!loading && column.truncated && (
            <button
              type="button"
              onClick={() => navigate(`/portfolio?stage=${stage.id}`)}
              className="mt-1 w-full rounded-md py-1.5 text-center text-xs font-medium text-indigo-600 hover:bg-indigo-50"
            >
              View all in Portfolio →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────

export default function Pipeline() {
  const stages = useStages();
  const { columns, loading, error, refetch } = useKanban();
  const navigate = useNavigate();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );
  const [activeId, setActiveId] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);  // mobile accordion

  const activeCard = activeId ? findCardById(activeId) : null;

  async function commitMove(cardId, fromStage, toStage) {
    moveCardOptimistically(cardId, fromStage, toStage);
    try {
      // Phase 36 P0 follow-up #7 — shared `api` instance so the 401
      // auto-retry interceptor applies on the very first drag right
      // after page load.
      await api.patch(
        `/deals/${cardId}/stage`,
        { stage: toStage, note: null }
      );
      invalidatePipelineCounts();
      const label = stages.find((s) => s.id === toStage)?.label || toStage;
      toast.success(`Moved to ${label}`);
    } catch (e) {
      revertCardMove(cardId, fromStage, toStage);
      toast.error(
        e?.response?.data?.detail || "Failed to update stage. Try again."
      );
    }
  }

  function handleDragEnd(event) {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const cardId = active.id;
    const card = findCardById(cardId);
    if (!card) return;
    const fromStage = card.pipeline_stage;
    // Drop target can be either a column (`column-<id>`) or another card
    // (we land in that card's column).
    let toStage;
    if (typeof over.id === "string" && over.id.startsWith("column-")) {
      toStage = over.id.replace("column-", "");
    } else {
      const overCard = findCardById(over.id);
      toStage = overCard?.pipeline_stage;
    }
    if (!toStage || toStage === fromStage) return;
    commitMove(cardId, fromStage, toStage);
  }

  // Default-expand the first non-empty stage on mobile so the user lands
  // somewhere meaningful instead of a wall of collapsed bars.
  useEffect(() => {
    if (expandedStage || !columns) return;
    const first = stages.find((s) => (columns[s.id]?.deals || []).length > 0);
    if (first) setExpandedStage(first.id);
  }, [columns, stages, expandedStage]);

  const orderedColumns = useMemo(
    () => stages.map((s) => ({ stage: s, column: columns?.[s.id] || { count: 0, deals: [] } })),
    [stages, columns]
  );

  return (
    <AppShell>
      <div className="pb-2" data-testid="pipeline-page">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="pipeline-title">
              Deal Pipeline
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Drag deals between stages to track progress.
            </p>
          </div>
        </div>

        {/* Phase 36 P0 follow-up #3 — graceful error banner. Stays
            ABOVE the board so the user retains all 8 column headers
            while the error is showing. */}
        {error ? (
          <div
            className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-testid="pipeline-error-banner"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              <span>
                {error === "unauthorized"
                  ? "Sign in again to load your pipeline."
                  : error === "timeout"
                    ? "Pipeline took too long to load. Tap retry."
                    : "Couldn't load your pipeline."}
              </span>
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              data-testid="pipeline-error-retry"
              className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
            >
              <RotateCcw className="h-3 w-3" /> Retry
            </button>
          </div>
        ) : null}

        {/* Desktop / tablet — Kanban. Header / pills / footers always
            render; only the deal-list body switches to skeleton on
            loading or empty-state on success+0. */}
        <div className="hidden md:block">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={(e) => setActiveId(e.active.id)}
            onDragCancel={() => setActiveId(null)}
            onDragEnd={handleDragEnd}
          >
            <div
              className="flex gap-4 overflow-x-auto pb-4"
              style={{ scrollSnapType: "x mandatory" }}
              data-testid="pipeline-board"
            >
              {orderedColumns.map(({ stage, column }) => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  column={column}
                  navigate={navigate}
                  loading={loading}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={null}>
              {activeCard ? (
                <DealCard deal={activeCard} dragging onClick={() => {}} />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* Mobile — accordion stack. Same loading-into-body pattern. */}
        <div className="flex flex-col gap-2 md:hidden" data-testid="pipeline-mobile-stack">
          {orderedColumns.map(({ stage, column }) => (
            <MobileAccordion
              key={stage.id}
              stage={stage}
              column={column}
              navigate={navigate}
              expanded={expandedStage === stage.id}
              onToggle={() => setExpandedStage(expandedStage === stage.id ? null : stage.id)}
              loading={loading}
            />
          ))}
        </div>
      </div>
    </AppShell>
  );
}
